import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Sha256 } from '@aws-crypto/sha256-browser';
import {
    CognitoIdentityClient,
    GetCredentialsForIdentityCommand,
    GetIdCommand,
} from '@aws-sdk/client-cognito-identity';
import * as Sentry from '@sentry/angular';
import { SignatureV4 } from '@smithy/signature-v4';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

import { environment } from '../../../environments/environment';
import { UnloadGuardService } from '../../services/unload-guard.service';
import { MonshinApi } from '../services/monshin.service';

/** ASCII記号文字 (!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~) にマッチする正規表現 */
const ASCII_SYMBOL_REGEX = /[!-/:-@[-`{-~]/;

/**
 * ASCII記号文字の入力を禁止するカスタムバリデータ
 * XSS対策として、フォーム入力値にASCII記号が含まれていないかチェックする
 *
 * @param control - 検証対象のフォームコントロール
 * @returns 記号が含まれる場合は { asciiSymbol: true }、それ以外はnull
 */
const noAsciiSymbolValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    if (ASCII_SYMBOL_REGEX.test(control.value)) {
        return { asciiSymbol: true };
    }
    return null;
};

@Component({
    selector: 'app-monshin-page',
    templateUrl: './monshin-page.component.html',
    styleUrls: ['./monshin-page.component.css'],
    standalone: false,
})
export class MonshinPageComponent implements OnInit {
    @ViewChild('inputFormat')
    inputFormat: any;

    checkStart = false;
    checkTerm = false;
    checkDisableTerm = false;
    checkAgreeTerm: any;
    page = 1;
    pageTotalView: any = '--';
    totalPage = 14;
    checkEndPage = false;
    formAgree: UntypedFormGroup;
    clickOne = false;
    year: any = '';
    month: any = '';
    date: any = '';
    japaneseEra = '';
    isBirthdayDateInvalid = false;
    private readonly MIN_BIRTH_YEAR = 1868;
    private readonly ERA_LIST = [
        { name: '令和', startYear: 2019 },
        { name: '平成', startYear: 1989 },
        { name: '昭和', startYear: 1926 },
        { name: '大正', startYear: 1912 },
        { name: '明治', startYear: 1868 },
    ];
    isDisable11thPage = true;
    diseaseCheckedList = [];
    checkScrollPage5 = false;
    checkScrollPage4 = false;
    errorRequired = '入力必須です';
    errorKana = 'カナで入力してください';
    errorInteger = '0~999までの整数を半角で入力してください';
    errorKanaMaxLength = '100文字以内で入力してください';
    errorAsciiSymbol = '記号文字は入力できません';
    errorDate = '日付が不正です';
    errorBirthday = '生年月日を正しく入力してください';
    validRequiredPostalCode = false;
    validLengthPostalCode = false;
    checkFocusLastName = false;
    checkFocusFirstName = false;
    checkFocusLastKana = false;
    checkFocusFirstKana = false;
    checkFocusPostalCode = false;
    checkFocusAddress = false;
    checkFocusPhone = false;
    checkFocusOtherDisease = false;
    checkFocusOtherAllergies = false;
    checkFocusSide = false;
    checkFocusPregnancy = false;
    checkFocusSmock = false;
    checkYear = false;
    checkMonth = false;
    checkDate = false;
    policy: string = '';
    isUsedPmAutomaticLinkageCode = false;

    diseaseList = [
        { name: '脳梗塞', checked: false },
        { name: 'てんかん', checked: false },
        { name: '認知症', checked: false },
        { name: '狭心症', checked: false },
        { name: '不整脈', checked: false },
        { name: '肝機能障害', checked: false },
        { name: '腎機能障害', checked: false },
        { name: '高血圧症', checked: false },
        { name: '1型糖尿病', checked: false },
        { name: '2型糖尿病', checked: false },
        { name: '脂質異常症', checked: false },
        { name: '胃炎・消化性潰瘍', checked: false },
        { name: '気管支喘息', checked: false },
        { name: '骨粗鬆症', checked: false },
        { name: '前立腺肥大症', checked: false },
        { name: '緑内障', checked: false },
        { name: '白内障', checked: false },
        { name: '鼻炎・花粉症', checked: false },
        { name: 'その他', checked: false },
    ];
    allergiesCheckValueList = [];
    allergiesValue = [
        { name: '卵', checked: false },
        { name: '牛乳', checked: false },
        { name: 'アルコール', checked: false },
        { name: 'ヨード', checked: false },
        { name: 'その他', checked: false },
    ];
    frequentlyEatFoodList = [
        { id: 0, name: '乳製品 ', checked: false },
        { id: 1, name: 'コーヒー ', checked: false },
        { id: 2, name: '納豆 ', checked: false },
        { id: 3, name: 'グレープフルーツ ', checked: false },
    ];
    frequentlyEatFoodListID = [];
    diathesisList = [
        { id: 0, name: 'かぶれやすい ', checked: false },
        { id: 1, name: '便秘がち ', checked: false },
        { id: 2, name: '下痢しやすい ', checked: false },
        { id: 3, name: '胃が弱い ', checked: false },
    ];
    diathesisListID = [];
    contactLensValueList = [
        { id: 0, name: 'ハード ', checked: false },
        { id: 1, name: 'ソフト ', checked: false },
        { id: 2, name: '使い捨て ', checked: false },
    ];
    contactLensValueListID = [];
    favoriteFoodLifestyleList = [
        { id: 0, name: '甘いものをよく食べる ', checked: false },
        { id: 1, name: '脂っこいものをよく食べる ', checked: false },
        { id: 2, name: '塩気の強いものが好き ', checked: false },
        { id: 3, name: '夕食後すぐ就寝する ', checked: false },
        { id: 4, name: '間食が多い ', checked: false },
        { id: 5, name: '朝食を抜くことが多い ', checked: false },
        { id: 6, name: '食べるのが早い ', checked: false },
        { id: 7, name: 'あまり運動をしない ', checked: false },
    ];
    favoriteFoodLifestyleListID = [];
    notGoodDosageFormList = [
        { id: 0, name: '粉 ', checked: false },
        { id: 1, name: 'シロップ ', checked: false },
        { id: 2, name: 'カプセル ', checked: false },
        { id: 3, name: '錠剤 ', checked: false },
    ];
    namePharmacy = '';
    pharmacyId = '';
    notGoodDosageFormListID = [];
    isSubmitted = false;
    api = environment.api;
    address = '';
    checkTermcheckBox = false;
    phoneNumber = '';
    isPostalCodeRegexInvalid = false;
    checkValidNumber = false;

    patternKana = '^([a-zA-Zァ-ーｧ-ﾝﾞﾟ]|ー)+$';
    patternNumber = '^[0-9]+$';

    pmAutomaticLinkageCode = '';

    @ViewChild('divMonshin')
    divMonshin: any;

    constructor(
        private fb: UntypedFormBuilder,
        protected monshinApi: MonshinApi,
        protected ngxSpinner: NgxSpinnerService,
        protected toaStr: ToastrService,
        private route: ActivatedRoute,
        private unloadGuard: UnloadGuardService,
    ) {
        this.formAgree = this.fb.group({});
    }

    async ngOnInit(): Promise<void> {
        const route = this.route.snapshot;
        this.pharmacyId = route.paramMap.get('id') || '';
        this.pmAutomaticLinkageCode = route.queryParamMap.get('pmAutomaticLinkageCode') || '';

        this.getNamePharmacy();
        this.buildForm();

        // Check for duplicate interview on page load if pmAutomaticLinkageCode is present
        if (this.pmAutomaticLinkageCode) {
            this.checkPmAutomaticLinkageCodeDuplicate();
        }
    }

    /**
     * ブラウザ・スマートフォンの戻る操作を画面内の「前へ」ボタンと同じ動作にする。
     *
     * 問診票は SPA 内のページ切替えだけで進むため、そのままでは履歴が積まれず、
     * 戻る操作でアプリ自体から離脱して入力値が消えてしまう。
     * クリック操作（ユーザージェスチャー）の中でページごとに履歴エントリを積み、
     * popstate で履歴の state から表示ページを復元する。
     * （ユーザージェスチャーなしで積んだ履歴は Chrome の history manipulation
     * intervention により戻る操作でスキップされるため）
     */
    @HostListener('window:popstate', ['$event'])
    onPopState({ state }: PopStateEvent): void {
        // 送信完了後はフォームに戻さない（通常のブラウザバックに委ねる）
        if (this.isSubmitted) return;
        const { term = null, monshinPage = null } = state ?? {};
        // 利用規約ページ表示中に戻る操作をした → 規約を閉じる（closeTerm() の history.back() もここに到達する）
        if (this.checkTerm && !term) {
            this.checkTerm = false;
            return;
        }
        // 規約を閉じたあとに「進む」で規約の履歴エントリに戻った → 規約を再表示する
        if (term) {
            this.checkTerm = true;
            return;
        }
        // 問診ページの履歴エントリ → state のページ番号を復元する（戻る・進むの両方に対応）
        if (monshinPage) {
            this.checkStart = true;
            this.page = monshinPage;
            setTimeout(() => {
                window.scroll(0, 0);
            });
        } else if (this.checkStart) {
            // 記入開始前の履歴まで戻った → 離脱確認のうえ、同意したらリロードして同意画面に戻す（入力値は破棄される）
            if (window.confirm('問診票の入力内容が破棄されますが、よろしいですか？')) {
                this.unloadGuard.reloadWithoutConfirmation();
            } else {
                // キャンセルした場合は履歴を進めて問診ページに留まる
                history.forward();
            }
        }
    }

    /** 利用規約を開く（戻る操作で閉じられるよう履歴エントリを積む） */
    openTerm(): void {
        this.checkTerm = true;
        history.pushState({ term: true }, '', location.href);
    }

    /** 利用規約を閉じる（履歴を戻して popstate 経由で閉じる） */
    closeTerm(): void {
        history.back();
    }

    /** 現在の問診ページの履歴エントリを積む（戻る操作でページを復元できるようにする） */
    private pushMonshinPageHistory(): void {
        history.pushState({ monshinPage: this.page }, '', location.href);
    }

    // @ts-ignore
    convertNumber(value: any) {
        const maxFractionDigits = 2;
        const option = {};
        // @ts-ignore
        option['maximumFractionDigits'] = maxFractionDigits;
        if (value != '') {
            const countDecimals =
                (value && value.toString() && value.toString().split('.').length > 1)
                    ? value.toString().split('.')[1].length
                    : 0;
            const toFixed = countDecimals > maxFractionDigits
                ? (countDecimals - maxFractionDigits)
                : maxFractionDigits;
            // @ts-ignore
            const valueToFixed = (maxFractionDigits == 0)
                ? Number(value * (10 ** maxFractionDigits))
                : Number((value * (10 ** maxFractionDigits)).toFixed(toFixed));
            return parseFloat(value) ? (Math.floor(valueToFixed) / (10 ** maxFractionDigits)) : 0;
        }
    }

    toASCII(chars: any) {
        let ascii = '';
        for (let i = 0, l = chars.length; i < l; i++) {
            let c = chars[i].charCodeAt(0);

            // make sure we only convert half-full width char
            if (c >= 0xFF00 && c <= 0xFFEF) {
                c = 0xFF & (c + 0x20);
            }

            ascii += String.fromCharCode(c);
        }

        return ascii;
    }

    getAddress(postalCode: any) {
        this.address = '';
        this.ngxSpinner.show();
        this.monshinApi.getAddress(postalCode).subscribe((res) => {
            this.ngxSpinner.hide().then(() => {
                if (res) {
                    const data = res.data[0];
                    this.address = data.prefecture + data.city + data.town + data.koaza;
                }
            });
        }, (error) => {
            // Sentryにエラーを送信
            Sentry.captureException(error, {
                tags: {
                    component: 'MonshinPageComponent',
                    method: 'getAddress',
                },
            });

            this.ngxSpinner.hide().then(() => {
                this.address = '';
            });
        });
    }

    /** Cognito Identity Pool から一時的な AWS クレデンシャルを取得する */
    private async getAwsCredentials(): Promise<{
        accessKeyId: string;
        secretAccessKey: string;
        sessionToken: string;
    }> {
        const client = new CognitoIdentityClient({ region: environment.region });
        const { IdentityId } = await client.send(
            new GetIdCommand({ IdentityPoolId: environment.IdentityPoolId }),
        );
        const { Credentials } = await client.send(
            new GetCredentialsForIdentityCommand({ IdentityId: IdentityId! }),
        );
        return {
            accessKeyId: Credentials!.AccessKeyId!,
            secretAccessKey: Credentials!.SecretKey!,
            sessionToken: Credentials!.SessionToken!,
        };
    }

    /** SigV4 署名付きで API Gateway にリクエストを送信し、レスポンス JSON を返す */
    private async invokeApi(
        invokeUrl: string,
        method: string,
        body?: any,
    ): Promise<any> {
        const credentials = await this.getAwsCredentials();
        const url = new URL(invokeUrl);
        // applyChecksum: false — x-amz-content-sha256 ヘッダーを付与しない。
        // API Gateway の CORS 設定で許可されていないため preflight エラーになる。
        const signer = new SignatureV4({
            credentials,
            region: environment.region,
            service: 'execute-api',
            sha256: Sha256,
            applyChecksum: false,
        });
        const request = {
            method,
            protocol: url.protocol,
            hostname: url.hostname,
            path: url.pathname,
            query: Object.fromEntries(url.searchParams),
            headers: {
                'Content-Type': 'application/json',
                host: url.hostname,
            } as Record<string, string>,
            ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        };
        const signed = await signer.sign(request);
        const response = await fetch(invokeUrl, {
            method: signed.method,
            headers: signed.headers as Record<string, string>,
            body: signed.body as string | undefined,
        });
        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
        return response.json();
    }

    /** LINE連携コードが使用済みかどうかを API で確認する */
    async checkPmAutomaticLinkageCodeDuplicate(): Promise<void> {
        try {
            this.ngxSpinner.show();
            const res = await this.invokeApi(
                this.api + '/check-pm-automatic-linkage-code',
                'POST',
                { pm_automatic_linkage_code: this.pmAutomaticLinkageCode },
            );
            if (res?.exists) {
                this.isUsedPmAutomaticLinkageCode = true;
            }
        } catch {
            this.toaStr.error('LINE連携コードのチェックに失敗しました', '', {
                positionClass: 'toast-center-center',
            });
        } finally {
            this.ngxSpinner.hide();
        }
    }

    /** 薬局 ID から薬局名・利用規約を取得して表示する */
    async getNamePharmacy(): Promise<void> {
        this.ngxSpinner.show();
        try {
            const res = await this.invokeApi(
                this.api + '/pharmacies/' + this.pharmacyId,
                'GET',
            );
            this.policy = res?.policy;
            this.namePharmacy = res?.name;
            this.checkDisableTerm = true;
        } catch (err: any) {
            if (
                !this.pharmacyId
                || err.message === 'Request failed with status code 404'
            ) {
                this.toaStr.error('不正なアクセスです', '', {
                    positionClass: 'toast-center-center',
                });
            } else {
                this.toaStr.error('インターネットに接続できません', '', {
                    positionClass: 'toast-center-center',
                });
            }
            this.checkDisableTerm = false;
        } finally {
            await this.ngxSpinner.hide();
        }
    }

    buildForm(val?: any) {
        this.formAgree = this.fb.group({
            // page1
            birthday: new UntypedFormControl(val ? val.birthday : '', []),
            first_name: new UntypedFormControl(val ? val.first_name : '', [
                Validators.required,
                noAsciiSymbolValidator,
            ]),
            last_name: new UntypedFormControl(val ? val.last_name : '', [
                Validators.required,
                noAsciiSymbolValidator,
            ]),
            last_name_kana: new UntypedFormControl(val ? val.last_name_kana : '', [
                Validators.required,
                Validators.maxLength(100),
                noAsciiSymbolValidator,
            ]),
            first_name_kana: new UntypedFormControl(val ? val.first_name_kana : '', [
                Validators.required,
                Validators.maxLength(100),
                noAsciiSymbolValidator,
            ]),
            sex: new UntypedFormControl(val ? val.sex : '', [Validators.required]),
            postal_code: new UntypedFormControl(val ? val.postal_code : '', [Validators.required]),
            address: new UntypedFormControl(val ? this.address : '', [Validators.required]),
            phone_number: new UntypedFormControl(val ? val.phoneNumber : '', [Validators.required]),
            // page2
            height: new UntypedFormControl(val ? val.height : '', []),
            weight: new UntypedFormControl(val ? val.weight : '', []),
            // page3
            prescription_note: new UntypedFormControl(val ? val.prescription_note : '', [
                Validators.required,
            ]),
            generic_state: new UntypedFormControl(val ? val.generic_state : '', [
                Validators.required,
            ]),
            // only choose generic_state = 3
            medicine_name: new UntypedFormControl(val ? val.medicine_name : '', []),
            written_in_prescription_note_desired_medicines: new UntypedFormControl(
                val ? val.written_in_prescription_note_desired_medicines : false,
                [],
            ),
            // page 4
            illness_state: new UntypedFormControl(val ? val.illness_state : '', [
                Validators.required,
            ]),
            illness_disease: new UntypedFormControl(this.diseaseCheckedList, []),
            other_disease: new UntypedFormControl(val ? val.other_disease : '', []),
            //  page5
            allergies_state: new UntypedFormControl(val ? val.allergies_state : '', [
                Validators.required,
            ]),
            allergies_value: new UntypedFormControl(this.allergiesCheckValueList, []),
            other_allergies: new UntypedFormControl(val ? val.other_allergies : '', []),
            //  page 6
            side_effect_state: new UntypedFormControl(val ? val.side_effect_state : '', [
                Validators.required,
            ]),
            side_effect_case: new UntypedFormControl(val ? val.side_effect_case : '', []),
            //  page 7
            adjuvant_remedy_state: new UntypedFormControl(val ? val.adjuvant_remedy_state : '', [
                Validators.required,
            ]),
            adjuvant_remedy_name: new UntypedFormControl(val ? val.adjuvant_remedy_name : '', []),
            written_in_prescription_note_adjuvant_remedy: new UntypedFormControl(
                val ? val.written_in_prescription_note_adjuvant_remedy : false,
                [],
            ),
            supplement_state: new UntypedFormControl(val ? val.supplement_state : '', [
                Validators.required,
            ]),
            supplement_name: new UntypedFormControl(val ? val.supplement_name : '', []),
            //  page 8
            pregnancy_state: new UntypedFormControl(val ? val.pregnancy_state : '', []),
            date_of_birth: new UntypedFormControl(val ? val.date_of_birth : '', []),
            breast_feeding: new UntypedFormControl(val ? val.breast_feeding : '', []),
            //  page 9
            drink: new UntypedFormControl(val ? val.drink : '', [Validators.required]),
            frequency: new UntypedFormControl(val ? val.frequency : '', []),
            drive: new UntypedFormControl(val ? val.drive : '', [Validators.required]),
            smoke_state: new UntypedFormControl(val ? val.smoke_state : '', [Validators.required]),
            smoke_count: new UntypedFormControl(val ? val.smoke_count : '', []),
            //  page 10
            dangerous_work: new UntypedFormControl(val ? val.dangerous_work : '', [
                Validators.required,
            ]),
            dangerous_hobby: new UntypedFormControl(val ? val.dangerous_hobby : '', [
                Validators.required,
            ]),
            meal_regularity: new UntypedFormControl(val ? val.meal_regularity : '', [
                Validators.required,
            ]),
            //  page 12
            contact_lens_state: new UntypedFormControl(val ? val.contact_lens_state : '', [
                Validators.required,
            ]),
        });

        // Check form change page 1
        this.formAgree.get('postal_code')?.valueChanges.subscribe((value) => {
            value.replace('-', '');
            const idZip = this.toASCII(value);

            const zip = idZip.replace(/(\d\d\d)(\d\d\d\d)/, '$1-$2');
            setTimeout(() => {
                const pattern = new RegExp('[0-9]{3}-?[0-9]{4}');
                const result = pattern.test(zip);
                if (this.formAgree.get('postal_code')?.value == '') {
                    this.validLengthPostalCode = false;
                    this.validRequiredPostalCode = true;
                    this.isPostalCodeRegexInvalid = false;
                } else {
                    this.validLengthPostalCode = false;
                    this.validRequiredPostalCode = false;
                    this.isPostalCodeRegexInvalid = !result;
                    const pattern1 = new RegExp('\\D');
                    const result1 = pattern1.test(idZip.replace('-', ''));
                    this.checkValidNumber = result1 ? false : true;
                    if (zip.length != 8 && zip.length != 0) {
                        this.validLengthPostalCode = true;
                        this.validRequiredPostalCode = false;
                        this.isPostalCodeRegexInvalid = false;
                        this.checkFocusPostalCode = true;
                    }
                }
            });
        });

        // Check form change page 4
        this.formAgree.get('illness_state')?.valueChanges.subscribe((value) => {
            if (value == 0) {
                const diseases = this.formAgree.get('illness_disease')?.value;
                if (diseases.length > 0) {
                    // @ts-ignore
                    diseases.map((disease) => {
                        if (disease.name == 'その他') {
                            this.formAgree.get('other_disease')?.setValidators(Validators.required);
                        }
                    });
                }
                this.formAgree.get('illness_disease')?.setValidators(Validators.required);
                this.formAgree.get('illness_disease')?.updateValueAndValidity();
            } else {
                this.formAgree.get('illness_disease')?.clearValidators();
                this.formAgree.get('other_disease')?.clearValidators();
                this.formAgree.get('illness_disease')?.updateValueAndValidity();
                this.formAgree.get('other_disease')?.updateValueAndValidity();
            }
        });

        // Check form change for page 5
        this.formAgree.get('allergies_state')?.valueChanges.subscribe((value) => {
            if (value == 0) {
                const allergies = this.formAgree.get('allergies_value')?.value;
                if (allergies.length > 0) {
                    // @ts-ignore
                    allergies.map((allergy) => {
                        if (allergy.name == 'その他') {
                            this.formAgree.get('other_allergies')?.setValidators(
                                Validators.required,
                            );
                        }
                    });
                }
                this.formAgree.get('allergies_value')?.setValidators(Validators.required);
                this.formAgree.get('allergies_value')?.updateValueAndValidity();
            } else {
                this.formAgree.get('allergies_value')?.clearValidators();
                this.formAgree.get('other_allergies')?.clearValidators();
                this.formAgree.get('allergies_value')?.updateValueAndValidity();
                this.formAgree.get('other_allergies')?.updateValueAndValidity();
            }
        });

        // Page 6
        this.formAgree.get('side_effect_state')?.valueChanges.subscribe((value) => {
            if (value == 0) {
                this.formAgree.get('side_effect_case')?.setValidators(Validators.required);
                this.formAgree.get('side_effect_case')?.updateValueAndValidity();
            } else {
                this.formAgree.get('side_effect_case')?.clearValidators();
                this.formAgree.get('side_effect_case')?.updateValueAndValidity();
            }
        });

        // Page 7
        this.formAgree.get('drink')?.valueChanges.subscribe((value) => {
            if (value == 0) {
                this.formAgree.get('frequency')?.setValidators(Validators.required);
                this.formAgree.get('frequency')?.updateValueAndValidity();
            } else {
                this.formAgree.get('frequency')?.clearValidators();
                this.formAgree.get('frequency')?.updateValueAndValidity();
            }
        });
        this.formAgree.get('smoke_state')?.valueChanges.subscribe((value) => {
            if (value == 0) {
                this.formAgree.get('smoke_count')?.setValidators(Validators.required);
                this.formAgree.get('smoke_count')?.updateValueAndValidity();
            } else {
                this.formAgree.get('smoke_count')?.clearValidators();
                this.formAgree.get('smoke_count')?.updateValueAndValidity();
            }
        });
        this.formAgree.get('contact_lens_state')?.valueChanges.subscribe((value) => {
            this.isDisable11thPage = this.contactLensValueListID?.length <= 0 && value == 0;
        });
        // page 8
        this.formAgree.get('sex')?.valueChanges.subscribe((value) => {
            if (value == 1) {
                this.formAgree.get('pregnancy_state')?.setValidators(Validators.required);
                this.formAgree.get('pregnancy_state')?.updateValueAndValidity();
                this.formAgree.get('breast_feeding')?.setValidators(Validators.required);
                this.formAgree.get('breast_feeding')?.updateValueAndValidity();
            } else {
                this.formAgree.get('pregnancy_state')?.clearValidators();
                this.formAgree.get('pregnancy_state')?.updateValueAndValidity();
                this.formAgree.get('breast_feeding')?.clearValidators();
                this.formAgree.get('breast_feeding')?.updateValueAndValidity();
            }
        });
        this.formAgree.get('pregnancy_state')?.valueChanges.subscribe((value) => {
            // 妊娠していない場合は出産予定日を空にする
            if (value == 1) {
                this.formAgree.get('date_of_birth')?.setValue('');
            }
        });
        return this.formAgree;
    }
    /** 問診票の記入を開始する */
    startFilling(): void {
        this.checkStart = true;
        this.pushMonshinPageHistory();
    }
    checkAgree(event: any) {
        this.checkAgreeTerm = event.target.checked;
    }

    previousPage(): any {
        if (this.page < 1 || this.page === 1) {
            return false;
        }
        // 履歴を戻し、popstate 経由で前のページを表示する（戻る操作と同じ経路）
        history.back();
    }

    nextPage(): any {
        if (this.page > this.totalPage || this.page === this.totalPage) {
            return false;
        } else if (this.page < this.totalPage) {
            this.page = this.page + 1;
            this.pushMonshinPageHistory();
        }
        setTimeout(() => {
            window.scroll(0, 0);
        });
    }

    get not_good_dosage_form(): UntypedFormArray {
        return this.formAgree.get('not_good_dosage_form') as UntypedFormArray;
    }

    processingBirthDay(): void {
        if (this.year === '' || this.month === '' || this.date === '') {
            this.formAgree.get('birthday')?.setValue('');
            return;
        }
        const paddedMonth = String(this.month).padStart(2, '0');
        const paddedDate = String(this.date).padStart(2, '0');
        const birthDay = `${this.year}-${paddedMonth}-${paddedDate}`;
        this.formAgree.get('birthday')?.setValue(birthDay);
    }
    async actionSave() {
        if (this.clickOne) return;
        this.clickOne = true;
        this.processingBirthDay();
        this.formAgree.removeControl('frequently_eat_food');
        this.formAgree.removeControl('diathesis');
        this.formAgree.removeControl('contact_lens_value');
        this.formAgree.removeControl('favorite_food_and_lifestyle');
        this.formAgree.removeControl('not_good_dosage_form');
        this.formAgree.removeControl('pharmacy_id');
        this.formAgree.markAllAsTouched();
        if (this.formAgree.invalid) {
            this.clickOne = false;
            return;
        }
        this.formAgree.addControl(
            'frequently_eat_food',
            new UntypedFormControl(this.frequentlyEatFoodListID, []),
        );
        this.formAgree.addControl('diathesis', new UntypedFormControl(this.diathesisListID, []));
        this.formAgree.addControl(
            'contact_lens_value',
            new UntypedFormControl(this.contactLensValueListID, []),
        );
        this.formAgree.addControl(
            'favorite_food_and_lifestyle',
            new UntypedFormControl(this.favoriteFoodLifestyleListID, []),
        );
        this.formAgree.addControl(
            'not_good_dosage_form',
            new UntypedFormControl(this.notGoodDosageFormListID, []),
        );
        this.formAgree.addControl('pharmacy_id', new UntypedFormControl(this.pharmacyId, []));

        if (this.pmAutomaticLinkageCode !== '') {
            this.formAgree.addControl(
                'pm_automatic_linkage_code',
                new UntypedFormControl(this.pmAutomaticLinkageCode, []),
            );
        }

        const data = this.formAgree.getRawValue();

        // Page 3
        if (data.generic_state != 3) {
            data.medicine_name = '';
            data.written_in_prescription_note_desired_medicines = false;
        }

        // Page 4
        if (data.illness_state != 0) {
            data.illness_disease = [];
            data.other_disease = '';
        }
        if (data.illness_state == 0) {
            let count = 0;
            // @ts-ignore
            data.illness_disease.map((disease) => {
                if (disease.name == 'その他') {
                    count = 1;
                }
            });
            if (count == 0) {
                data.other_disease = '';
            }
        }

        // Page 5
        if (data.allergies_state != 0) {
            data.allergies_value = [];
            data.other_allergies = '';
        }
        if (data.allergies_state == 0) {
            let count = 0;
            // @ts-ignore
            data.allergies_value.map((allergy) => {
                if (allergy.name == 'その他') {
                    count = 1;
                }
            });
            if (count == 0) {
                data.other_allergies = '';
            }
        }

        // Page 6
        if (data.side_effect_state != 0) {
            data.side_effect_case = '';
        }

        // Page 7
        if (data.adjuvant_remedy_state != 0) {
            data.adjuvant_remedy_name = '';
            data.written_in_prescription_note_adjuvant_remedy = false;
        } else if (data.adjuvant_remedy_name == '') {
            // 「はい」かつ、薬剤名記入なしの場合
            data.adjuvant_remedy_name = '併用薬あり';
        }

        if (data.supplement_state != 0) {
            data.supplement_name = '';
        } else if (data.supplement_name == '') {
            // 「はい」かつ、薬剤名記入なしの場合
            data.supplement_name = '併用薬（OTC）あり';
        }
        // page 8
        if (data.sex == 0) {
            data.pregnancy_state = '';
            data.breast_feeding = '';
            data.date_of_birth = '';
        }

        // Page 9
        if (data.drink != 0) {
            data.frequency = '';
        }
        if (data.smoke_state != 0) {
            data.smoke_count = '';
        }

        // Page 12
        if (data.contact_lens_state != 0) {
            data.contact_lens_value = [];
        }

        this.ngxSpinner.show();
        try {
            await this.invokeApi(this.api + '/interview', 'POST', data);
            this.isSubmitted = true;
            this.checkEndPage = true;
        } catch {
            this.toaStr.error('登録に失敗しました', '', {
                positionClass: 'toast-center-center',
            });
            this.clickOne = false;
        } finally {
            await this.ngxSpinner.hide();
        }
    }

    changeCheckDisease(event: any) {
        const checked = event.target.checked;
        this.diseaseList.forEach((it) => {
            if (event.target.value === it.name) {
                it.checked = checked;
            }
        });
        this.commonDiseaseValidation(event);
    }

    checkInputValueOfDiseaseText(event: any) {
        this.diseaseList.forEach((item) => {
            if (item.name === 'その他') {
                item.checked = event.target.textLength > 0;
            }
        });
        this.commonDiseaseValidation(event);
    }

    commonDiseaseValidation(event: any) {
        // @ts-ignore
        this.diseaseCheckedList = this.diseaseList.filter((disease) => disease.checked);
        this.formAgree.get('illness_disease')?.setValue(this.diseaseCheckedList);
        if (event.target.value == 'その他' && event.target.checked) {
            this.checkScrollPage4 = true;
            this.formAgree.get('other_disease')?.setValidators(Validators.required);
            this.formAgree.get('other_disease')?.updateValueAndValidity();
        }
        if (event.target.value == 'その他' && !event.target.checked) {
            this.checkScrollPage4 = false;
            this.formAgree.get('other_disease')?.clearValidators();
            this.formAgree.get('other_disease')?.updateValueAndValidity();
        }
    }

    async checkAllergiesValue(event: any) {
        const checked = event.target.checked;
        await this.allergiesValue.forEach((it) => {
            if (event.target.value === it.name) {
                it.checked = checked;
            }
        });
        this.commonAllergiesValidation(event);
    }

    checkInputValueOfAllergiesText(event: any) {
        this.allergiesValue.forEach((item) => {
            if (item.name === 'その他') {
                item.checked = event.target.textLength > 0;
            }
        });
        this.commonAllergiesValidation(event);
    }

    commonAllergiesValidation(event: any) {
        // @ts-ignore
        this.allergiesCheckValueList = this.allergiesValue.filter((allergy) => allergy.checked);
        this.formAgree.get('allergies_value')?.setValue(this.allergiesCheckValueList);
        if (event.target.value == 'その他' && event.target.checked) {
            this.checkScrollPage5 = true;
            this.formAgree.get('other_allergies')?.setValidators(Validators.required);
            this.formAgree.get('other_allergies')?.updateValueAndValidity();
        }
        if (event.target.value == 'その他' && !event.target.checked) {
            this.checkScrollPage5 = false;
            this.formAgree.get('other_allergies')?.clearValidators();
            this.formAgree.get('other_allergies')?.updateValueAndValidity();
        }
    }

    async checkFrequentlyEatFood(event: any) {
        const checked = event.target.checked;
        this.frequentlyEatFoodList.forEach((it) => {
            if (event.target.value == it.id) {
                it.checked = checked;
            }
        });
        this.frequentlyEatFoodListID = [];
        await this.frequentlyEatFoodList.forEach((value) => {
            if (value.checked) {
                // @ts-ignore
                this.frequentlyEatFoodListID.push(value.id);
            }
        });
    }
    async checkDiathesis(event: any) {
        const checked = event.target.checked;
        this.diathesisList.forEach((it) => {
            if (event.target.value == it.id) {
                it.checked = checked;
            }
        });
        this.diathesisListID = [];
        await this.diathesisList.forEach((value) => {
            if (value.checked === true) {
                // @ts-ignore
                this.diathesisListID.push(value.id);
            }
        });
    }
    async checkContactLensValue(event: any) {
        const checked = event.target.checked;
        this.contactLensValueList.forEach((it) => {
            if (event.target.value == it.id) {
                it.checked = checked;
            }
        });
        this.contactLensValueListID = [];
        await this.contactLensValueList.forEach((value) => {
            if (value.checked === true) {
                // @ts-ignore
                this.contactLensValueListID.push(value.id);
            }
        });
        this.isDisable11thPage = this.contactLensValueListID.length <= 0;
    }
    async checkFavoriteFoodLifestyleList(event: any) {
        const checked = event.target.checked;
        this.favoriteFoodLifestyleList.forEach((it) => {
            if (event.target.value == it.id) {
                it.checked = checked;
            }
        });
        this.favoriteFoodLifestyleListID = [];
        await this.favoriteFoodLifestyleList.forEach((value) => {
            if (value.checked === true) {
                // @ts-ignore
                this.favoriteFoodLifestyleListID.push(value.id);
            }
        });
    }
    async checkNotGoodDosageForm(event: any) {
        const checked = event.target.checked;
        this.notGoodDosageFormList.forEach((it) => {
            if (event.target.value == it.id) {
                it.checked = checked;
            }
        });
        this.notGoodDosageFormListID = [];
        await this.notGoodDosageFormList.forEach((value) => {
            if (value.checked === true) {
                // @ts-ignore
                this.notGoodDosageFormListID.push(value.id);
            }
        });
    }

    isDisableButton(): any {
        if (this.page == 1) {
            return this.formAgree.get('first_name')?.invalid
                || this.formAgree.get('last_name')?.invalid
                || this.formAgree.get('last_name_kana')?.invalid
                || this.formAgree.get('first_name_kana')?.invalid
                || this.formAgree.get('sex')?.invalid || this.formAgree.get('postal_code')?.invalid
                || this.formAgree.get('address')?.invalid
                || this.formAgree.get('phone_number')?.invalid
                || this.year == '' || this.month == '' || this.date == ''
                || this.isBirthdayDateInvalid
                || this.isPostalCodeRegexInvalid || this.validLengthPostalCode;
        }
        if (this.page == 2) {
            return this.formAgree.get('height')?.invalid || this.formAgree.get('weight')?.invalid;
        }
        if (this.page == 3) {
            return this.formAgree.get('prescription_note')?.invalid
                || this.formAgree.get('generic_state')?.invalid
                || this.formAgree.get('written_in_prescription_note_desired_medicines')?.invalid;
        }
        if (this.page == 4) {
            return this.formAgree.get('illness_state')?.invalid
                || this.formAgree.get('illness_disease')?.invalid
                || this.formAgree.get('other_disease')?.invalid;
        }

        if (this.page == 5) {
            return this.formAgree.get('allergies_state')?.invalid
                || this.formAgree.get('allergies_value')?.invalid
                || this.formAgree.get('other_allergies')?.invalid;
        }
        if (this.page == 6) {
            return this.formAgree.get('side_effect_state')?.invalid
                || this.formAgree.get('side_effect_case')?.invalid;
        }
        if (this.page == 7) {
            return this.formAgree.get('adjuvant_remedy_state')?.invalid
                || this.formAgree.get('supplement_state')?.invalid;
        }
        if (this.page == 8 && this.formAgree.get('sex')?.value == 1) {
            return this.formAgree.get('pregnancy_state')?.invalid
                || this.formAgree.get('breast_feeding')?.invalid
                || this.formAgree.get('date_of_birth')?.invalid;
        }
        if (this.page == (this.pageTotalView == 14 ? 9 : 8)) {
            return this.formAgree.get('drink')?.invalid || this.formAgree.get('frequency')?.invalid
                || this.formAgree.get('drive')?.invalid
                || this.formAgree.get('smoke_state')?.invalid
                || this.formAgree.get('smoke_count')?.invalid;
        }
        if (this.page == (this.pageTotalView == 14 ? 10 : 9)) {
            return this.formAgree.get('dangerous_work')?.invalid
                || this.formAgree.get('dangerous_hobby')?.invalid
                || this.formAgree.get('meal_regularity')?.invalid;
        }
        if (this.page == (this.pageTotalView == 14 ? 12 : 11)) {
            return this.formAgree.get('contact_lens_state')?.invalid || this.isDisable11thPage;
        }
        return false;
    }

    onBirthdayInput(): void {
        this.isBirthdayDateInvalid = false;
    }

    onYearInput(): void {
        this.japaneseEra = '';
        this.isBirthdayDateInvalid = false;
    }

    private padField(value: any): any {
        if (value !== '' && /^\d+$/.test(String(value))) {
            return String(value).padStart(2, '0');
        }
        return value;
    }

    updateJapaneseEra(): void {
        const y = Number(this.year);
        const isYearValid = /^\d+$/.test(String(this.year))
            && y >= this.MIN_BIRTH_YEAR
            && y <= new Date().getFullYear();
        this.japaneseEra = isYearValid ? this.toJapaneseEra(y) : '';
    }

    private validateBirthdayDate(): void {
        this.isBirthdayDateInvalid = this.year !== '' && this.month !== '' && this.date !== ''
            && !this.isValidBirthdayValues();
    }

    private isValidBirthdayValues(): boolean {
        if (this.year === '' || this.month === '' || this.date === '') {
            return false;
        }
        if (
            !/^\d+$/.test(String(this.year))
            || !/^\d+$/.test(String(this.month))
            || !/^\d+$/.test(String(this.date))
        ) {
            return false;
        }
        const y = Number(this.year);
        const m = Number(this.month);
        const d = Number(this.date);
        const currentYear = new Date().getFullYear();
        if (y < this.MIN_BIRTH_YEAR || y > currentYear) {
            return false;
        }
        if (m < 1 || m > 12) {
            return false;
        }
        if (d < 1 || d > this.daysInMonth(y, m)) {
            return false;
        }
        return true;
    }

    private daysInMonth(year: number, month: number): number {
        return new Date(year, month, 0).getDate();
    }

    /**
     * 西暦の年を和暦表記に変換する。
     *
     * ERA_LIST は新しい元号順に並んでおり、上から見て最初に
     * `year >= startYear` を満たした元号を「その年に属する（新しい側の）元号」とする。
     * 元号の年数は `西暦 - (開始年 - 1)` で求める（例: 平成 = 1989開始なので 1990 → 平成2年）。
     *
     * 【改元年（2元号併記）の判定】
     * 各元号は年の途中（例: 平成は1989/1/8、令和は2019/5/1）から始まるため、
     * 「西暦 === いずれかの元号の開始年」のときは、その年は年初が前の元号・途中から新元号、
     * という2つの元号にまたがる年になる。
     * この場合は `前の元号の最終年・新元号の元年`（例: 昭和64年・平成元年）を併記する。
     * ただし明治（リスト最後）は1つ前の元号を持たないため、明治元年のみ単独表示になる。
     */
    private toJapaneseEra(year: number): string {
        for (let i = 0; i < this.ERA_LIST.length; i++) {
            const era = this.ERA_LIST[i];
            if (year < era.startYear) {
                continue;
            }
            const eraName = `${era.name}${this.formatEraYear(year - (era.startYear - 1))}`;
            const prevEra = this.ERA_LIST[i + 1];
            // 開始年と一致 かつ 前の元号が存在する場合は改元年なので2元号を併記する
            if (year === era.startYear && prevEra) {
                const prevEraName = `${prevEra.name}${
                    this.formatEraYear(year - (prevEra.startYear - 1))
                }`;
                return `${prevEraName}・${eraName}`;
            }
            return eraName;
        }
        return '';
    }

    private formatEraYear(eraYear: number): string {
        return eraYear === 1 ? '元年' : `${eraYear}年`;
    }

    isValidYear() {
        this.checkYear = this.year === '';
        this.updateJapaneseEra();
        this.validateBirthdayDate();
        this.processingBirthDay();
    }
    isValidMonth() {
        this.checkMonth = this.month === '';
        this.month = this.padField(this.month);
        this.validateBirthdayDate();
        this.processingBirthDay();
    }
    isValidDate() {
        this.checkDate = this.date === '';
        this.date = this.padField(this.date);
        this.validateBirthdayDate();
        this.processingBirthDay();
    }

    onTrim(item: any): void {
        if (item === 'last_name') {
            this.checkFocusLastName = true;
        }
        if (item == 'first_name') {
            this.checkFocusFirstName = true;
        }
        if (item === 'last_name_kana') {
            this.checkFocusLastKana = true;
        }
        if (item === 'first_name_kana') {
            this.checkFocusFirstKana = true;
        }
        if (item === 'postal_code') {
            this.checkFocusPostalCode = true;
            if (this.formAgree.get('postal_code')?.value == '') {
                this.validLengthPostalCode = false;
                this.validRequiredPostalCode = true;
                this.isPostalCodeRegexInvalid = false;
            }
            const value = this.formAgree.get('postal_code')?.value;
            const idZip = this.toASCII(value.replace('-', ''));
            const postalCode = idZip.replace(/(\d\d\d)(\d\d\d\d)/, '$1-$2');
            this.formAgree.get('postal_code')?.setValue(postalCode, { emitEvent: false });
            setTimeout(() => {
                if (
                    postalCode.length === 8 && !this.isPostalCodeRegexInvalid
                    && this.formAgree.controls['postal_code'].dirty
                ) {
                    this.getAddress(postalCode);
                    this.checkFocusAddress = false;
                    this.formAgree.controls['postal_code'].markAsPristine();
                }
            });
        }
        setTimeout(() => {
            if (item === 'address') {
                this.checkFocusAddress = true;
            }
        });

        if (item === 'phone_number') {
            this.checkFocusPhone = true;
        }
        if (item === 'other_disease') {
            this.checkFocusOtherDisease = true;
        }
        if (item === 'other_allergies') {
            this.checkFocusOtherAllergies = true;
        }
        if (item === 'side_effect_case') {
            this.checkFocusSide = true;
        }

        // @ts-ignore
        if (this.formAgree.get(item)?.value && this.formAgree.get(item)?.dirty) {
            // @ts-ignore
            this.formAgree.get(item)?.setValue(this.formAgree.get(item)?.value.trim());
        }
    }
    sexChange(event: any) {
        const checkValue = event.target.value;
        if (checkValue == 0) {
            this.pageTotalView = 13;
        } else {
            this.pageTotalView = 14;
        }
    }

    closeBrow() {
        setTimeout(() => {
            // 送信済みで破棄される入力値はないため、離脱確認は表示しない
            this.unloadGuard.reloadWithoutConfirmation();
        });
    }
}
